import { ManagementListToolbar } from "@taunggyi-plus/design-system";
import { RecordFormDialog } from "@taunggyi-plus/design-system";
import { formatRecordDate } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { Card, PaginatedList } from "@taunggyi-plus/design-system";
export type AdPackage = {
  id: string;
  name: string;
  placement: string;
  duration_days: number;
  fee_minor: number;
  slots: number;
  active: boolean;
};
type Campaign = {
  id: string;
  title: string;
  status: string;
  starts_on: string;
  ends_on: string;
  paid: boolean;
  views: number;
  clicks: number;
};
export function AdPackages({ admin = false }: { admin?: boolean }) {
  const { client } = useAuth();
  const [packages, setPackages] = useState<AdPackage[]>([]),
    [campaigns, setCampaigns] = useState<Campaign[]>([]),
    [edit, setEdit] = useState<AdPackage | null>(null),
    [message, setMessage] = useFeedback(),
    [busy, setBusy] = useState(false),
    [query, setQuery] = useState(""),
    [status, setStatus] = useState("all");
  const [packageQuery, setPackageQuery] = useState("");
  const filteredPackages = packages.filter((pkg) =>
    pkg.name.toLowerCase().includes(packageQuery.toLowerCase()),
  );
  const load = useCallback(async () => {
    if (!client) return;
    const [p, c] = await Promise.all([
      client.from("ad_packages").select("*"),
      client.rpc("ad_campaign_results"),
    ]);
    if (p.error || c.error) throw new Error(p.error?.message ?? c.error?.message);
    setPackages((p.data ?? []) as AdPackage[]);
    setCampaigns((c.data ?? []) as Campaign[]);
  }, [client]);
  useEffect(() => {
    const timer = setTimeout(() => void load().catch((e) => setMessage(e.message)), 0);
    return () => clearTimeout(timer);
  }, [load, setMessage]);
  return (
    <section>
      {!admin && <h2>Advertising packages & results</h2>}

      {message && <p role="status">{message}</p>}
      <ManagementListToolbar
        query={packageQuery}
        onQueryChange={setPackageQuery}
        searchLabel="Search packages"
        resultCount={filteredPackages.length}
        onClear={() => setPackageQuery("")}
        actions={
          admin && (
            <RecordFormDialog
              open={Boolean(edit)}
              onClose={() => setEdit(null)}
              title={edit ? "Edit package" : "Create package"}
            >
              <form
                key={edit?.id ?? "new"}
                className="form-grid"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!client || busy) return;
                  const f = new FormData(e.currentTarget);
                  setBusy(true);
                  try {
                    const r = await client.rpc("save_ad_package", {
                      id: edit?.id ?? null,
                      name: f.get("name"),
                      placement: f.get("placement"),
                      days: Number(f.get("days")),
                      fee: Math.round(Number(f.get("fee")) * 100),
                      slots: Number(f.get("slots")),
                      active: f.has("active"),
                    });
                    if (r.error) throw new Error(r.error.message);
                    await load();
                    setEdit(null);
                    setMessage("Package saved.");
                  } catch (e) {
                    setMessage(e instanceof Error ? e.message : "Could not save.");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <label className="field">
                  Name
                  <input
                    name="name"
                    defaultValue={edit?.name}
                    required
                    minLength={3}
                    maxLength={120}
                  />
                </label>
                <label className="field">
                  Placement
                  <select name="placement" defaultValue={edit?.placement ?? "home"}>
                    <option value="home">Customer home</option>
                    <option value="service">Service page</option>
                  </select>
                </label>
                {[
                  ["days", "Duration (days)", edit?.duration_days ?? 7, 365],
                  ["fee", "Fee (MMK)", (edit?.fee_minor ?? 10000) / 100, 1000000000],
                  ["slots", "Concurrent slots", edit?.slots ?? 5, 100],
                ].map(([name, label, value, max]) => (
                  <label className="field" key={name}>
                    {label}
                    <input
                      name={String(name)}
                      type="number"
                      min={1}
                      max={Number(max)}
                      defaultValue={Number(value)}
                      required
                    />
                  </label>
                ))}
                <label>
                  <input name="active" type="checkbox" defaultChecked={edit?.active ?? true} />
                  Available to providers
                </label>
                <button className="button" disabled={busy}>
                  Save package
                </button>
                {edit && (
                  <button
                    className="button button--secondary"
                    type="button"
                    onClick={() => setEdit(null)}
                  >
                    Cancel edit
                  </button>
                )}
              </form>
            </RecordFormDialog>
          )
        }
      />
      <PaginatedList
        items={filteredPackages}
        resetKey={packageQuery}
        label="Advertising package pages"
        renderItem={(p) => (
          <Card key={p.id}>
            <h3>{p.name}</h3>
            <p>
              {p.placement} · {p.duration_days} days · {(p.fee_minor / 100).toLocaleString()} MMK ·{" "}
              {p.slots} slots · {p.active ? "Available" : "Disabled"}
            </p>
            {admin && (
              <button className="button" onClick={() => setEdit(p)}>
                Edit
              </button>
            )}
          </Card>
        )}
      />
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search campaigns"
        resultCount={
          campaigns.filter(
            (c) =>
              (status === "all" || c.status === status) &&
              c.title.toLowerCase().includes(query.toLowerCase()),
          ).length
        }
        filters={[
          {
            label: "Status",
            value: status,
            onChange: setStatus,
            options: ["all", "pending", "approved", "rejected", "cancelled"].map((value) => ({
              value,
              label: value === "all" ? "All statuses" : value,
            })),
          },
        ]}
        onClear={() => {
          setQuery("");
          setStatus("all");
        }}
      />
      <PaginatedList
        items={campaigns.filter(
          (c) =>
            (status === "all" || c.status === status) &&
            c.title.toLowerCase().includes(query.toLowerCase()),
        )}
        resetKey={`${query}:${status}`}
        label="Campaign pages"
        renderItem={(c) => (
          <Card key={c.id}>
            <h3>{c.title}</h3>
            <p>
              {c.status} · {c.paid ? "Paid" : "Awaiting payment"} · {formatRecordDate(c.starts_on)}{" "}
              – {formatRecordDate(c.ends_on)}
            </p>
            <p>
              {c.views} customer views · {c.clicks} clicks
            </p>
          </Card>
        )}
      />
    </section>
  );
}
